import Icon from 'site/components/ui/Icon.tsx'
import MatchDevice from 'apps/website/matchers/device.ts'
import { matchCanonical } from '$store/utils/matchCanonical.ts'
import { type DateMatcher, matchDate } from '$store/utils/matchDate.ts'
import { useSignal } from '@preact/signals'
import { useEffect, useRef } from 'preact/hooks'
import { useCart } from 'apps/vtex/hooks/useCart.ts'
import { blockKey, isBlocked } from 'site/sdk/useLocalStorage.ts'

import { ImageWidget as LiveImage } from 'apps/admin/widgets.ts'
import { LoaderContext } from 'deco/types.ts'

/**
 * @title Configuração de cupom
 * @titleBy coupon
 */
export interface Content {
    /** @title Código do cupom */
    coupon: string
    /** @title Título */
    title?: string
    /** @title Sub título */
    subtitle?: string
    /** @title Avisos */
    warnings?: string[]

    /**
     * @title Tempo de inatividade (ms)
     * @description Tempo, em mili segundos, de inatividade até que o modal apareça
     */
    idleTime?: number

    /**
     * @title Tempo de bloqueio (horas)
     * @description O modal não aparecerá por x horas quando o usuário fecha-lo
     */
    blockedTime?: number

    /** @title Opções de acionamento */
    action?: {
        /** @title Ação ao clicar */
        actionType?: 'copy' | 'apply'
        /** @title Texto do botão */
        buttonText?: string
        /** @title Texto do botão ao clicar */
        buttonTextClicked?: string
        /** @title Mensagem exibida ao clicar */
        message?: string
        /** @title Ao clicar, redirecionar para */
        redirect?: string
    }
    /**@title Ativar distância entre as imagens */
    gap?: boolean
    /** @title Imagem */
    image?: {
        /** @title Imagem desktop */
        desktop: LiveImage
        /** @title Imagem mobile */
        mobile: LiveImage
        /** @title Texto alternativo */
        altText?: string
    }
    imageRight?: {
        /** @title Imagem desktop */
        desktop: LiveImage
        /** @title Imagem mobile */
        mobile: LiveImage
        /** @title Texto alternativo */
        altText?: string
    }

    /** @title Matcher de URL */
    matcher?: string
    /** @title Agendamento */
    dateMatcher?: DateMatcher
}

export interface Props {
    /** @title Configuração de cupons */
    contents?: Content[]
}

export const loader = (props: Props, req: Request, ctx: LoaderContext) => {
    const { pathname } = new URL(req.url)

    const contents = (props.contents ?? [])
        .filter(({ dateMatcher }) => !dateMatcher || matchDate(dateMatcher))
        .filter(({ matcher }) => !matcher || matchCanonical(matcher, pathname))

    const isMobile = ctx.device !== 'desktop'

    return { ...props, contents, isMobile }
}

const { addCouponsToCart } = useCart()

function CouponModal(
    { contents: [content], isMobile }: ReturnType<typeof loader>,
) {
    if (!content) return null

    const {
        coupon,
        idleTime = 7000,
        title,
        subtitle,
        warnings = [],
        blockedTime = 24,
        gap,
        image,
        imageRight,
        action,
    } = content

    const { desktop, mobile, altText } = image ?? {}
    const { desktop: desktopRight, mobile: mobileRight, altText: altTextRight } = imageRight ?? {}
    const {
        actionType = 'copy',
        buttonText = 'Copiar',
        buttonTextClicked = 'Copiado!',
        message = 'Cupom copiado com sucesso!',
        redirect,
    } = action ?? {}

    const clicked = useSignal(false)
    const open = useSignal(false)
    const blocked = useSignal(true)

    const foregroundRef = useRef<HTMLDivElement>(null)

    const closeModal = () => {
        blocked.value = true
        blockKey({ key: 'modal-cooldown', time: blockedTime, unity: 'hrs' })
    }

    // Verifica se o modal não deve abrir por ter sido fechado recentemente
    useEffect(() => {
        blocked.value = isBlocked('modal-cooldown')
    }, [])

    // Define a aparição do modal
    useEffect(() => {
        let taskID: ReturnType<typeof setTimeout>

        const debounceAction = () => {
            clearTimeout(taskID)
            taskID = setTimeout(() => open.value = true, idleTime)
        }

        // Eventos que recomeçam a contagem de tempo (desktop e mobile)
        document.addEventListener('touchstart', debounceAction)
        document.addEventListener('touchmove', debounceAction)
        document.addEventListener('touchend', debounceAction)

        // Quando o usuário tirar o mouse da página (desktop)
        document.addEventListener('mouseleave', () => open.value = true)
    }, [])

    // Trata o click no botão (copiar/aplicar)
    const handleActionClick = async () => {
        clicked.value = true

        navigator.clipboard.writeText(coupon)
        if (actionType === 'apply') await addCouponsToCart({ text: coupon })

        const time = actionType === 'apply' ? 5000 : 10000

        setTimeout(() => {
            clicked.value = false
            closeModal()

            if (redirect) window.location.href = redirect
            else if (actionType === 'apply') window.location.reload()
        }, time)
    }

    const shown = open.value && !blocked.value

    return (
        <div
            class={`fixed top-0 left-0 z-[60] h-full w-full flex items-center justify-center ${
                shown ? 'block' : 'hidden'
            }`}
        >
            <div
                class='absolute w-full h-full left-0 top-0 bg-black/70'
                onClick={closeModal}
            />

            <div
                class={`relative flex max-md:mx-3 flex-col max-w-[70%] rounded-[10px] overflow-hidden lg:flex-row lg:w-auto ${
                    gap && 'gap-3'
                }`}
                ref={foregroundRef}
            >
                {/* Close */}
                <button
                    class='absolute top-2 right-2 lg:top-1 lg:right-1 w-8 h-8 lg:w-[42px] lg:h-[42px] text-white duration-200 lg:text-[#0D0D0D] hover:scale-110 z-[1]'
                    onClick={closeModal}
                >
                    <Icon
                        id='X'
                        size={32}
                        width={32}
                        height={32}
                    />
                </button>

                {/* Image */}
                <div class='flex items-center justify-center'>
                    <img class='w-full' src={isMobile ? mobile : desktop} alt={altText} />
                </div>

                <div
                    class={`flex flex-col justify-center gap-5 lg:px-[60px] lg:pt-[30px] lg:pb-[30px] px-5 pt-5 pb-8 relative isolate ${
                        !imageRight && 'bg-[#F9F9F9]'
                    }`}
                >
                    {imageRight && (
                        <img
                            class='w-full h-full absolute -z-10 left-0'
                            loading='lazy'
                            src={isMobile ? mobileRight : desktopRight}
                            alt={altTextRight}
                        />
                    )}
                    {/* Titles */}
                    <div class='flex flex-col gap-2'>
                        <span class='text-lg sm:text-xl font-extrabold text-[#222454]'>
                            {title}
                        </span>
                        <span class='text-base font-normal text-[#0D0D0D]'>{subtitle}</span>
                    </div>

                    {/* Coupon code */}
                    <div class='flex h-[46px] border-2 border-[#0ABB9D] rounded-lg bg-white lg:w-[300px]'>
                        <span class='flex items-center justify-center grow h-full text-sm font-bold text-[#707173] uppercase'>
                            {coupon}
                        </span>
                        <button
                            class='flex items-center h-full bg-[#0ABB9D] border-0 text-white text-sm font-extrabold leading-6 uppercase px-6 duration-200 hover:bg-[#08A087]'
                            data-coupon={coupon}
                            onClick={handleActionClick}
                        >
                            {clicked.value ? buttonTextClicked : buttonText}
                        </button>
                    </div>

                    {/* Warnings */}
                    <ul class={`flex-col gap-1 ${clicked.value ? 'hidden' : 'flex'}`}>
                        {warnings?.map((warning) => (
                            <li class='text-[#4C4C4C] text-xs'>{warning}</li>
                        ))}
                    </ul>

                    {/* "On copied" dialog */}
                    <div
                        class={`w-full items-center gap-3 p-5 bg-[#0ABB9D] lg:w-[300px] ${
                            clicked.value ? 'flex' : 'hidden'
                        }`}
                    >
                        <Icon
                            class='text-white shrink-0'
                            id='Checked'
                            size={21}
                            width={21}
                            height={21}
                        />
                        <span class='text-white text-sm font-bold leading-5'>
                            {message}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CouponModal
